import { apiPaths } from 'api/kinerja/kepala-sekolah/config'
import { useMutationApi, useQueryApi } from 'utils/api'

import type { CustomError } from 'types/custom-error'
import type {
  ParamsBulkKsPenilaianPeriodikPegawai,
  ParamsBulkKsSendPeriodikToBkn,
  ParamsGetKsPeriodikDetail,
  ParamsGetKsPeriodikList,
  ParamsGetKsPeriodikPegawaiDetail,
  ParamsResetPenilaianPeriodikKs,
  ResponseGetKsPeriodikDetail,
  ResponseGetKsPeriodikList,
  ResponseGetKsPeriodikPegawaiDetail,
} from 'types/kinerja-dinas'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

export const API_GET_KS_PERIODIK_LIST_QUERY_KEY =
  'API_GET_KS_PERIODIK_LIST_QUERY_KEY'

export function useApiGetKsPeriodikList({
  params,
  options,
}: {
  params: ParamsGetKsPeriodikList
  options?: UseQueryOptions<ResponseGetKsPeriodikList>
}) {
  const { skpYear } = params

  return useQueryApi<ResponseGetKsPeriodikList>(
    `${apiPaths.dinas}/penilaian/periodik`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [API_GET_KS_PERIODIK_LIST_QUERY_KEY, { skpYear }],
    }
  )
}

export const API_GET_KS_PERIODIK_DETAIL_QUERY_KEY =
  'API_GET_KS_DINAS_PERIODIK_DETAIL_QUERY_KEY'

export function useApiGetKsPeriodikDetail({
  params,
  options,
}: {
  params: ParamsGetKsPeriodikDetail
  options?: UseQueryOptions<ResponseGetKsPeriodikDetail>
}) {
  const { skpYear, skpMonth } = params

  return useQueryApi<ResponseGetKsPeriodikDetail>(
    `${apiPaths.dinas}/penilaian/periodik/detail`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [API_GET_KS_PERIODIK_DETAIL_QUERY_KEY, { skpYear, skpMonth }],
    }
  )
}

export const API_GET_KS_PERIODIK_PEGAWAI_DETAIL_QUERY_KEY =
  'API_GET_KS_PERIODIK_PEGAWAI_DETAIL_QUERY_KEY'

export function useApiGetKsPeriodikPegawaiDetail({
  params,
  options,
}: {
  params: ParamsGetKsPeriodikPegawaiDetail
  options?: UseQueryOptions<ResponseGetKsPeriodikPegawaiDetail>
}) {
  const { skpYear, skpMonth, ptkId } = params

  return useQueryApi<ResponseGetKsPeriodikPegawaiDetail>(
    `${apiPaths.dinas}/penilaian/periodik/detail/${ptkId}`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_GET_KS_PERIODIK_PEGAWAI_DETAIL_QUERY_KEY,
        { skpYear, skpMonth, ptkId },
      ],
    }
  )
}

export function useApiBulkKsPenilaianPeriodikPegawai(
  opts?: UseMutationOptions<
    unknown,
    CustomError,
    ParamsBulkKsPenilaianPeriodikPegawai
  >
) {
  return useMutationApi<
    unknown,
    CustomError,
    ParamsBulkKsPenilaianPeriodikPegawai
  >(`${apiPaths.dinas}/penilaian/periodik/bulk`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      ...opts,
    },
  })
}

export function useApiBulkKsSendPeriodikToBkn(
  opts?: UseMutationOptions<unknown, CustomError, ParamsBulkKsSendPeriodikToBkn>
) {
  return useMutationApi<unknown, CustomError, ParamsBulkKsSendPeriodikToBkn>(
    `${apiPaths.dinas}/penilaian/periodik/send-bkn/bulk`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}

export function useApiResetPenilaianPeriodikKs(
  userId: string,
  opts?: UseMutationOptions<
    unknown,
    CustomError,
    ParamsResetPenilaianPeriodikKs
  >
) {
  return useMutationApi<unknown, CustomError, ParamsResetPenilaianPeriodikKs>(
    `${apiPaths.dinas}/penilaian/periodik/reset/${userId}`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}
